import merge from 'lodash-es/merge';
import noop from 'lodash-es/noop';
import pickBy from 'lodash-es/pickBy';
import { stringify } from 'qs';

import { NotificationsService } from '@jane/monolith/services/notifications';
import { getJwt } from '@jane/shared/auth';
import type { TaggedCheckoutError } from '@jane/shared/data-access';
import { parseCheckoutErrorDetails } from '@jane/shared/data-access';
import {
  isInMaintenance,
  maintenanceReload,
  monolithPaths as paths,
} from '@jane/shared/util';

let onUnauthorized: () => void = noop;

export const setOnUnauthorized = (handler: () => void) => {
  onUnauthorized = handler;
};

function filteredParams(params: object) {
  const filteredParams = pickBy(params, Boolean);

  return `?${stringify(filteredParams, { arrayFormat: 'brackets' })}`;
}

const handleErrorsForAppMode =
  (appMode = { type: 'default' }, url: string) =>
  (response: any | undefined) => {
    if (!response) return null;

    if (response.error && appMode.type === 'headless') {
      NotificationsService.notifyErrorToIFrameParent(
        response.error,
        url,
        'HTTPRequestError'
      );
    }

    if (response.errors) {
      const { error, validations, status } = response.errors;

      if (validations) return { validations };
      if (status === 401) {
        onUnauthorized();
      }

      if (appMode.type === 'headless') {
        NotificationsService.notifyErrorToIFrameParent(
          error || 'Server error has occurred',
          url,
          'HTTPRequestError'
        );
      }

      if (error) {
        const details = parseCheckoutErrorDetails(response.errors);

        if (details) {
          // No toast: CartAlerts shows the refusal.
          const tagged: TaggedCheckoutError = new Error(error);
          tagged.checkoutErrorDetails = details;
          throw tagged;
        }

        NotificationsService.error(error);
        throw error;
      }
      NotificationsService.error('Server error has occurred');
      throw response;
    }

    return response;
  };

const handleEmpty = (response: Response) => {
  if (response.ok) return;
  else {
    return {
      errors: { error: response.statusText, status: response.status },
    };
  }
};

function parseJSON(response: Response) {
  if (isInMaintenance(response)) {
    maintenanceReload();
    return;
  }

  if (response.status === 204) return;
  return response.text().then((text) => {
    if (text.trim() === '') {
      return handleEmpty(response);
    } else return JSON.parse(text);
  });
}

export function request(
  url: string,
  params: RequestInit & { requireAuth?: boolean } = {},
  queryParams?: object,
  // a request is simple if it does not trigger CORS. See https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests
  simple?: boolean
) {
  if (queryParams) {
    url += filteredParams(queryParams);
  }

  if (simple) {
    return fetch(url, params)
      .then(parseJSON)
      .then(handleErrorsForAppMode(paths.getAppMode(), url));
  }

  const defaultParams: {
    credentials: RequestCredentials;
    headers: Record<string, string>;
  } = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  const jwt = getJwt();
  if (jwt) {
    defaultParams.headers['Authorization'] = jwt;
  }

  return fetch(url, merge({}, defaultParams, params))
    .then(parseJSON)
    .then(handleErrorsForAppMode(paths.getAppMode(), url));
}

export function requestWithoutErrorHandling(
  url: string,
  params: RequestInit,
  queryParams?: object
) {
  const defaultParams: {
    credentials: RequestCredentials;
    headers: Record<string, string>;
  } = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  const jwt = getJwt();
  if (jwt) {
    defaultParams.headers['Authorization'] = jwt;
  }

  if (queryParams) {
    url += filteredParams(queryParams);
  }

  return fetch(url, merge({}, defaultParams, params)).then(parseJSON);
}

export async function loginRequest(
  url: string,
  params: RequestInit,
  queryParams?: object
) {
  const defaultParams = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  if (queryParams) {
    url += filteredParams(queryParams);
  }

  const response = await fetch(url, merge({}, defaultParams, params));
  const body = await response.json();
  const headers = { Authorization: response.headers.get('Authorization') };

  if (isInMaintenance(response)) {
    maintenanceReload();
  }

  return { body, headers };
}
